import { useState } from 'react';
import type { ReactNode } from 'react';
import { useIngredientSearch } from '../api/planner';
import type { IngredientOption } from '../api/planner';

/** Search the canonical ingredients and pick one.
 *
 * Two screens want this and they want it for opposite reasons — the profile
 * is choosing food to rule out, the pantry is listing food it has — so what
 * belongs beside each name differs: a recipe count is what makes ruling
 * something out a considered decision, and is noise when you are stood in
 * front of a cupboard. That line is the `meta` slot rather than a flag,
 * which is what lets the third caller say something neither of these two
 * would have thought of.
 *
 * What does not differ is the searching, and that is the part worth having
 * once: the two-character floor before anything is fetched, the results
 * ordered commonest-first, and clearing the box on a pick so the next search
 * starts from empty.
 */
export default function IngredientPicker({
  placeholder,
  onPick,
  meta,
  omit,
  excludeArtifacts,
  autoFocus,
}: {
  placeholder: string;
  onPick: (option: IngredientOption) => void;
  /** What to show after the name — a recipe count, what is already in stock. */
  meta?: (option: IngredientOption) => ReactNode;
  /** Ingredient ids to leave out, e.g. ones already on the list being built. */
  omit?: number[];
  /** Drop rows that were never food. See the pantry's use of it. */
  excludeArtifacts?: boolean;
  autoFocus?: boolean;
}) {
  const [term, setTerm] = useState('');
  const { data: found } = useIngredientSearch(term, { excludeArtifacts });

  const results = (found ?? []).filter((option) => !omit?.includes(option.id));

  function pick(option: IngredientOption) {
    onPick(option);
    setTerm('');
  }

  return (
    <>
      <input
        type="search"
        value={term}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        onChange={(event) => setTerm(event.target.value)}
      />

      {results.length > 0 && (
        <ul className="picker__results">
          {results.map((option) => (
            <li key={option.id}>
              <button type="button" onClick={() => pick(option)}>
                {option.name}
                {meta && <span className="muted small"> · {meta(option)}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
